import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScheduleVisitData } from '@/types/visit.types';
import { Lead } from '@/types/lead.types';
import { visitsService } from '@/services/visits.service';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

interface SalesKit {
  id: string;
  name: string;
  location: string;
}

export const ScheduleVisit: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get('lead_id');

  const [formData, setFormData] = useState<ScheduleVisitData>({
    lead_id: preselectedLeadId || '',
    sales_kit_id: '',
    scheduled_date: '',
    scheduled_time: '',
  });

  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lead search state
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  // Sales kits state (mocked for now)
  const [salesKits] = useState<SalesKit[]>([
    { id: '1', name: 'Sunshine Residency', location: 'Andheri West, Mumbai' },
    { id: '2', name: 'Green Valley Apartments', location: 'Powai, Mumbai' },
    { id: '3', name: 'Skyline Heights', location: 'Worli, Mumbai' },
  ]);

  // Fetch preselected lead
  useEffect(() => {
    const fetchPreselectedLead = async () => {
      if (preselectedLeadId) {
        try {
          const response = await leadsService.getById(preselectedLeadId);
          // API interceptor unwraps { success, data } - backend returns { lead: {...} }
          setSelectedLead(response.data?.lead || null);
          setFormData((prev) => ({ ...prev, lead_id: preselectedLeadId }));
        } catch (err) {
          console.error('Error fetching preselected lead:', err);
        }
      }
    };

    fetchPreselectedLead();
  }, [preselectedLeadId]);

  // Search leads
  const searchLeads = useCallback(async (query: string) => {
    if (query.length < 2) {
      setLeadResults([]);
      return;
    }

    try {
      setIsSearchingLeads(true);
      const response = await leadsService.getAll({ search: query }, 1, 10);
      setLeadResults(response.data?.data ?? []);
    } catch (err) {
      console.error('Error searching leads:', err);
      setLeadResults([]);
    } finally {
      setIsSearchingLeads(false);
    }
  }, []);

  // Debounced lead search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (leadSearch && !selectedLead) {
        searchLeads(leadSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [leadSearch, selectedLead, searchLeads]);

  const handleLeadSelect = (lead: Lead) => {
    setSelectedLead(lead);
    setFormData((prev) => ({ ...prev, lead_id: lead.id }));
    setLeadSearch('');
    setLeadResults([]);
    setShowLeadDropdown(false);
    setErrors((prev) => ({ ...prev, lead_id: '' }));
  };

  const handleClearLead = () => {
    setSelectedLead(null);
    setFormData((prev) => ({ ...prev, lead_id: '' }));
    setLeadSearch('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.lead_id) {
      newErrors.lead_id = 'Please select a lead';
    }

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'Please select a date';
    } else {
      const selectedDate = new Date(formData.scheduled_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.scheduled_date = 'Date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const dataToSubmit: ScheduleVisitData = {
        lead_id: formData.lead_id,
        scheduled_date: formData.scheduled_date,
      };

      if (formData.sales_kit_id) {
        dataToSubmit.sales_kit_id = formData.sales_kit_id;
      }

      if (formData.scheduled_time) {
        dataToSubmit.scheduled_time = formData.scheduled_time;
      }

      const response = await visitsService.schedule(dataToSubmit);
      navigate(`/visits/${response.data.visit.id}`);
    } catch (err: unknown) {
      console.error('Error scheduling visit:', err);
      const error = err as { response?: { data?: { message?: string } } };
      const errorMessage = error.response?.data?.message || 'Failed to schedule visit. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/visits')}
        className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Visits
      </button>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Schedule Visit</h1>
        <p className="text-secondary-600 mt-1">
          Schedule a property visit for a lead
        </p>
      </div>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lead Selector */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Lead <span className="text-red-500">*</span>
            </label>
            {selectedLead ? (
              <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                <div>
                  <p className="font-medium text-secondary-900">{selectedLead.name}</p>
                  <p className="text-sm text-secondary-500">{selectedLead.phone}</p>
                </div>
                <button
                  type="button"
                  onClick={handleClearLead}
                  className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  placeholder="Search by name or phone..."
                  value={leadSearch}
                  onChange={(e) => {
                    setLeadSearch(e.target.value);
                    setShowLeadDropdown(true);
                  }}
                  onFocus={() => setShowLeadDropdown(true)}
                  error={errors.lead_id}
                  leftIcon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  }
                  rightIcon={
                    isSearchingLeads ? (
                      <div className="w-5 h-5 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    ) : undefined
                  }
                />

                {/* Lead Search Results Dropdown */}
                {showLeadDropdown && leadResults.length > 0 && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowLeadDropdown(false)}
                    />
                    <div className="absolute z-20 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {leadResults.map((lead) => (
                        <button
                          key={lead.id}
                          type="button"
                          onClick={() => handleLeadSelect(lead)}
                          className="w-full text-left px-4 py-3 hover:bg-secondary-50 transition-colors border-b border-secondary-100 last:border-b-0"
                        >
                          <p className="font-medium text-secondary-900">{lead.name}</p>
                          <p className="text-sm text-secondary-500">{lead.phone}</p>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {showLeadDropdown && leadSearch.length >= 2 && leadResults.length === 0 && !isSearchingLeads && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg p-4">
                    <p className="text-sm text-secondary-500 text-center">No leads found</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sales Kit Selector */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Project (Optional)
            </label>
            <select
              value={formData.sales_kit_id}
              onChange={(e) => setFormData({ ...formData, sales_kit_id: e.target.value })}
              className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a project...</option>
              {salesKits.map((kit) => (
                <option key={kit.id} value={kit.id}>
                  {kit.name} - {kit.location}
                </option>
              ))}
            </select>
          </div>

          {/* Date Picker */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Visit Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => {
                setFormData({ ...formData, scheduled_date: e.target.value });
                setErrors((prev) => ({ ...prev, scheduled_date: '' }));
              }}
              min={minDate}
              className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.scheduled_date ? 'border-red-500' : 'border-secondary-300'
              }`}
            />
            {errors.scheduled_date && (
              <p className="mt-1.5 text-sm text-red-600">{errors.scheduled_date}</p>
            )}
          </div>

          {/* Time Picker */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Visit Time (Optional)
            </label>
            <input
              type="time"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
              className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Notes Field */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any additional notes about this visit..."
              className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{errors.submit}</p>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-secondary-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/visits')}
              disabled={isSubmitting}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              fullWidth
            >
              Schedule Visit
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ScheduleVisit;
